/**
 * The question box at the top of every input block's editor.
 *
 * All eleven configured their own RichTextEditor with the same fourteen lines, and the parts that
 * drifted were exactly the ones nobody looks at twice: three different mechanisms for putting the
 * cursor in the box on open (Numerical had none, so creating one left the cursor nowhere), and
 * three blocks that focused on create but not when the editing panel opened. Those are fixed and
 * this exists so they cannot come back.
 *
 * `value` and `onChange` deliberately stay at the call site. Blocks persist in four different
 * ways — some read a ref live at push time, some keep a render-time snapshot in lockstep, one uses
 * a fields reducer — and that is a property of the block's save model, not of this box. Unifying
 * the persistence would be a deep change to how those blocks store everything, with nothing to
 * show for it on screen. What belongs here is every rendered property, which is now identical by
 * construction.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextEditor } from './rich-text-editor';

import type { SharedEditorToolbar } from './use-shared-editor-toolbar';
import type { Editor } from '@tiptap/core';
import type { MutableRefObject } from 'react';

export interface BlockQuestionEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** The block's one toolbar slot, so the question box shares it with option rows and feedback. */
  toolbar: SharedEditorToolbar;
  /** Block-owned: FITB reaches for it to insert a blank chip into the passage. */
  editorRef: MutableRefObject<Editor | null>;
  /** Block-owned: set from onActivate, when a card is clicked into editing. */
  shouldFocusRef: MutableRefObject<boolean>;
  /** True in the right-side editing panel, which opens ready to type. */
  embedded?: boolean;
  /**
   * Overrides the shared "Enter your question" ghost text. FITB is the only caller that should:
   * it authors a passage with blanks in it, not a question.
   */
  placeholder?: string;
}

export function BlockQuestionEditor({
  value, onChange, toolbar, editorRef, shouldFocusRef, embedded, placeholder,
}: BlockQuestionEditorProps) {
  const { t } = useTranslation();
  const { activeEditorRef, setActiveEditor } = toolbar;

  /**
   * Focus on open, by the ONE mechanism. Not RichTextEditor's `autoFocus` prop, which does the
   * same job but trips jsx-a11y/no-autofocus — eight of those errors were this line, eleven times.
   */
  const onEditorReady = useCallback((e: Editor) => {
    editorRef.current = e;
    activeEditorRef.current = e;
    setActiveEditor(e);
    if (shouldFocusRef.current || embedded) { shouldFocusRef.current = false; e.commands.focus(); }
  }, [editorRef, activeEditorRef, setActiveEditor, shouldFocusRef, embedded]);

  const onEditorFocus = useCallback(() => {
    const e = editorRef.current;
    if (e) { activeEditorRef.current = e; setActiveEditor(e); }
  }, [editorRef, activeEditorRef, setActiveEditor]);

  return (
    <RichTextEditor
      content={value}
      onChange={onChange}
      // Every block's question lives at `data.question`, so one tag here covers all eleven.
      fieldId='question'
      placeholder={placeholder ?? t('blocks.questionEditor.placeholder')}
      editorClassName='min-h-20'
      hideToolbar
      onEditorReady={onEditorReady}
      onEditorFocus={onEditorFocus}
    />
  );
}
